import { useTranslation } from 'react-i18next';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import WhatsAppFloat from '../../home/components/WhatsAppFloat';
import SEO from '@/components/SEO';

export default function AdministrationPage() {
  const { t } = useTranslation('navigation');

  const solutions = [
    {
      titleKey: 'administrationSol1Title', descKey: 'administrationSol1Desc', icone: 'ri-user-line',
      details_fr: ['Registre citoyen numérique', 'Gestion des identités', 'Historique des services rendus', 'Suivi des demandes citoyennes', 'Interface citoyenne en ligne'],
      details_en: ['Digital citizen register', 'Identity management', 'Service history', 'Citizen request tracking', 'Online citizen interface']
    },
    {
      titleKey: 'administrationSol2Title', descKey: 'administrationSol2Desc', icone: 'ri-file-text-line',
      details_fr: ['Numérisation des documents', 'Archivage électronique sécurisé', 'Workflow de validation', 'Recherche avancée', 'Signature électronique'],
      details_en: ['Document scanning', 'Secure electronic archiving', 'Validation workflow', 'Advanced search', 'Electronic signature']
    },
    {
      titleKey: 'administrationSol3Title', descKey: 'administrationSol3Desc', icone: 'ri-smartphone-line',
      details_fr: ['Demandes en ligne', 'Suivi des dossiers', 'Paiements électroniques', 'Rendez-vous en ligne', 'Notifications automatiques'],
      details_en: ['Online requests', 'File tracking', 'Electronic payments', 'Online appointments', 'Automatic notifications']
    },
    {
      titleKey: 'administrationSol4Title', descKey: 'administrationSol4Desc', icone: 'ri-calculator-line',
      details_fr: ['Comptabilité publique', 'Gestion budgétaire', 'Suivi des dépenses', 'Reporting financier', 'Contrôle de gestion'],
      details_en: ['Public accounting', 'Budget management', 'Expense tracking', 'Financial reporting', 'Management control']
    },
    {
      titleKey: 'administrationSol5Title', descKey: 'administrationSol5Desc', icone: 'ri-team-line',
      details_fr: ['Dossiers agents publics', 'Gestion des carrières', 'Planning des services', 'Évaluations de performance', 'Formation continue'],
      details_en: ['Public agent files', 'Career management', 'Service planning', 'Performance evaluations', 'Continuous training']
    },
    {
      titleKey: 'administrationSol6Title', descKey: 'administrationSol6Desc', icone: 'ri-dashboard-line',
      details_fr: ['KPI services publics', 'Satisfaction citoyenne', 'Délais de traitement', 'Rapports d\'activité', 'Analyses prédictives'],
      details_en: ['Public service KPIs', 'Citizen satisfaction', 'Processing times', 'Activity reports', 'Predictive analytics']
    }
  ];

  const isEn = t('home') === 'Home';

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Logiciel Administration Publique - Alpha Solutions | E-Gouvernance"
        description="Solutions pour administration publique : registre citoyen, gestion documentaire, services en ligne, comptabilité publique, ressources humaines et tableaux de bord."
        keywords="e-gouvernance Cameroun, logiciel administration publique, services citoyens digitaux, gestion documentaire publique"
        canonicalPath="/secteurs/administration"
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Logiciel Administration Publique Alpha Solutions",
          "description": "Solutions e-gouvernance pour administrations publiques au Cameroun",
          "brand": { "@type": "Organization", "name": "Alpha Solutions" }
        }}
      />
      <Header />

      <section
        className="relative bg-cover bg-center py-20"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6),rgba(0,0,0,0.6)),url('https://readdy.ai/api/search-image?query=modern%20african%20government%20office%20digital%20citizen%20management%20system%20public%20administration%20technology%20professional%20public%20service%20facility%20electronic%20document%20management%20citizen%20services&width=1200&height=600&seq=administration-hero-v2&orientation=landscape')` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">{t('administrationHeroTitle')}</h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">{t('administrationHeroDesc')}</p>
          <a href="https://wa.me/237693385267" target="_blank" rel="noopener noreferrer" className="inline-block bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-whatsapp-line mr-2"></i>{t('sectorDemoBtn')}
          </a>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('administrationSolutionsTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('administrationSolutionsSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((sol, i) => (
              <div key={i} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-slate-700 text-white rounded-full flex items-center justify-center mb-4">
                  <i className={`${sol.icone} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t(sol.titleKey)}</h3>
                <p className="text-gray-600 mb-4">{t(sol.descKey)}</p>
                <ul className="space-y-2">
                  {(isEn ? sol.details_en : sol.details_fr).map((f, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-2 mt-1"></i>
                      <span className="text-sm text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('administrationWhyTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'ri-eye-line', title: t('administrationAdv1Title'), desc: t('administrationAdv1Desc') },
              { icon: 'ri-speed-up-line', title: t('administrationAdv2Title'), desc: t('administrationAdv2Desc') },
              { icon: 'ri-customer-service-line', title: t('administrationAdv3Title'), desc: t('administrationAdv3Desc') },
              { icon: 'ri-money-dollar-circle-line', title: t('administrationAdv4Title'), desc: t('administrationAdv4Desc') },
            ].map((adv, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-slate-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${adv.icon} text-2xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{adv.title}</h3>
                <p className="text-gray-600">{adv.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-50 rounded-lg p-8 border-l-4 border-slate-700">
            <div className="flex items-center mb-6">
              <img src="https://readdy.ai/api/search-image?query=professional%20african%20port%20authority%20director%20in%20modern%20administrative%20office%20confident%20public%20administration%20manager%20overseeing%20port%20operations%20digital%20management%20systems&width=100&height=100&seq=port-douala-v2&orientation=squarish" alt={t('administrationTestiName')} className="w-16 h-16 rounded-full object-cover mr-4" />
              <div>
                <h3 className="text-xl font-bold text-slate-900">{t('administrationTestiName')}</h3>
                <p className="text-slate-700">{t('administrationTestiRole')}</p>
              </div>
            </div>
            <blockquote className="text-lg text-slate-800 italic mb-4">"{t('administrationTestiQuote')}"</blockquote>
            <div className="flex items-center">
              <div className="flex text-yellow-400">{[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill"></i>)}</div>
              <span className="ml-2 text-slate-700 font-medium">{t('administrationTestiSince')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">{t('administrationCtaTitle')}</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">{t('administrationCtaDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://wa.me/237693385267" target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line mr-2"></i>{t('sectorFreeDemo')}
            </a>
            <a href="/solutions" className="bg-white text-slate-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">{t('sectorViewSolutions')}</a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
